import {useCallback, useState} from 'react'
import {Button} from '@primer/react'
import {CopilotIcon} from '@primer/octicons-react'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useAgenticAutofixJobPoll} from '@github-ui/code-scanning-shared/hooks/use-agentic-autofix-job-poll'
import {
  AgenticAutofixError,
  handleAgenticAutofixErrorResponse,
} from '@github-ui/code-scanning-shared/types/agentic-autofix-error'
import {displayFlashMessage} from '@github-ui/code-scanning-shared/utils/display-flash-message'

export type AgenticAutofixInitialState = 'unassigned' | 'pending'

export interface AssignToCopilotProps {
  url: string
  alertNumber: number
  commentId: number
  repository: {owner: string; name: string}
  initialState?: AgenticAutofixInitialState
  agenticAutofixJobId?: string
}

type AssignToCopilotResponse = {agenticAutofixJobId?: string}

export function AssignToCopilotButton({
  url,
  alertNumber,
  commentId,
  repository,
  initialState,
  agenticAutofixJobId,
}: AssignToCopilotProps) {
  const [jobId, setJobId] = useState<string | null>(initialState === 'pending' ? (agenticAutofixJobId ?? null) : null)

  const handleJobError = useCallback((message: string) => {
    displayFlashMessage('error', `Copilot failed to create a pull request. ${message}.`)
    setJobId(null)
  }, [])

  const {isComplete} = useAgenticAutofixJobPoll(repository.owner, repository.name, jobId, handleJobError)

  const {mutate, isPending} = useMutation({
    mutationFn: async (): Promise<AssignToCopilotResponse> => {
      const response = await reactFetchJSON(url, {
        method: 'POST',
        body: {alert_number: alertNumber, comment_id: commentId},
      })
      if (!response.ok) {
        await handleAgenticAutofixErrorResponse(response)
      }
      return (await response.json()) as AssignToCopilotResponse
    },
    onSuccess: data => {
      if (data.agenticAutofixJobId) {
        setJobId(data.agenticAutofixJobId)
      }
    },
    onError: (error: Error) => {
      const message = error instanceof AgenticAutofixError ? error.message : 'Something went wrong, please try again.'
      displayFlashMessage('error', message)
    },
  })

  if (isComplete) {
    return null
  }

  const isWorking = isPending || jobId !== null

  return (
    <div className="d-flex flex-justify-end">
      <Button
        type="button"
        size="small"
        leadingVisual={CopilotIcon}
        loading={isWorking}
        loadingAnnouncement="Assigning to Copilot"
        inactive={isWorking}
        onClick={isWorking ? undefined : () => mutate()}
        data-testid="assign-to-copilot-button"
      >
        Assign to Copilot
      </Button>
    </div>
  )
}
